import * as uuid from 'uuid';
import EventService from '../../../helpers/classes/EventService';
import HttpService from '../../../helpers/classes/HttpService';
import LanguageService from '../../../helpers/classes/LanguageService';
import RenderingService from '../../../helpers/classes/RenderingService';
import SocketService, { SocketEvents } from '../../../helpers/classes/SocketService';
import TemplateService from '../../../helpers/classes/TemplateService';
import DataService, { DataStoreType } from '../../classes/DataService';
import { JourneyContainer } from '../../classes/JourneyContainer';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { ScannerAction, ScannerError, ScannerState, ScanProcess } from '../../enums/Scanner';
import { Templates } from '../../enums/Templates';
import { TranslationKey } from '../../enums/Translation';
import { IIdScanData } from '../../interfaces/DataFormats';
import { IScannerState } from '../../interfaces/ScannerState';
import * as lottie from 'lottie-web/build/player/lottie';
import { AnimationService } from '../../../helpers/classes/AnimationService';
import { FlowState } from '../../enums/FlowState';
import { InputProvider } from '../../enums/InputProvider';
import { TripleScanGuidanceProvider } from './triple-scan-guidance';
import { HighLevelResult } from '../../enums/HighLevelResult';

export class ScannerProvider {
	public static render(this: JourneyContainer) {
		return TemplateService.getElements(this._container, {
			providerContainer: '[data-jcs-element="provider__container"]',
		})
			.then(({ providerContainer }) => TemplateService.renderToContainer(providerContainer, Templates.Scanner))
			.then((providerContainer) => TemplateService.getElements(providerContainer, {
				action: '[data-jcs-element="scanner__action"]',
				scannerAnimationContainer: '[data-jcs-element="scanner__animation"]',
				loadingAnimationContainer: '[data-jcs-element="image__processing"]',
				failureReasonContainer: '[data-jcs-element="failure__reason__section"]'
			}))
			.then(({ action, scannerAnimationContainer, loadingAnimationContainer, failureReasonContainer }) => {
				if (scannerAnimationContainer !== undefined && loadingAnimationContainer !== undefined) {
					ScannerProvider.scannerAnimation = AnimationService.CreateAnimation(scannerAnimationContainer, this.assetsFolder + '/animations/scan-green.json', true, false, {});
					ScannerProvider.loadingAnimation = AnimationService.CreateAnimation(loadingAnimationContainer, this.assetsFolder + '/animations/loading.json', true, false, {});
					RenderingService.hideObject(failureReasonContainer);
					ScannerProvider.loadingAnimation!.play();
				}
				if (action instanceof HTMLButtonElement) {
					RenderingService.setEnabled(action, false);
					RenderingService.setText(action, LanguageService.getTranslation(TranslationKey.SCANNER_DESC_LOADING));
				}

			})
			.then(() => {
				if ((HttpService.auth === true || this._config.auth === false) && ScannerProvider._socketReady === false) {
					return ScannerProvider.startSocket.call(this);
				} else {
					return Promise.resolve();
				}
			})
			.then(() => EventService.publish(JourneyEvent.DISPLAY_PROVIDER, { provider: Templates.Scanner }));
	}

	/**
	 * Start socket communications
	 */
	public static startSocket(this: JourneyContainer) {
		return SocketService.initialise()
			.then(() => {
				SocketService.addListener(SocketEvents.UPDATE, ScannerProvider.onSocketUpdate.bind(this));
				SocketService.addListener(SocketEvents.RESULT, ScannerProvider.onSocketResult.bind(this));
			})
			.then(() => SocketService.start(this._journeyDefinitionId, this._journey !== undefined ? this._journey.id : uuid.v4()))
			.then((scannerState: IScannerState) => {
				ScannerProvider.processMessage.call(this, scannerState);
				ScannerProvider._socketReady = true;
			})
			.catch(console.warn);
	}

	private static _socketReady: boolean = false;
	private static scannerAnimation: lottie.AnimationItem | undefined;
	private static loadingAnimation: lottie.AnimationItem | undefined;

	private static processMessage(this: JourneyContainer, state: IScannerState): Promise<void> {
		if (state.connected !== true) {
			if (state.error === ScannerError.IS_LOCKED) {
				return ScannerProvider.updateScannerUI.call(this, {
					actionEvent: ScannerAction.REGISTER,
					actionText: TranslationKey.SCANNER_ACTION_REGISTER,
					titleText: TranslationKey.SCANNER_TITLE_BUSY,
					error: TranslationKey.SCANNER_DESC_BUSY,
				});
			} else if (state.error === ScannerError.IS_OFFLINE || state.error === ScannerError.IS_RELEASED) {
				return ScannerProvider.updateScannerUI.call(this, {
					actionEvent: ScannerAction.REGISTER,
					actionText: TranslationKey.SCANNER_ACTION_REGISTER,
					titleText: TranslationKey.SCANNER_TITLE_OFFLINE,
					error: TranslationKey.SCANNER_DESC_OFFLINE,
				});
			} else {
				return ScannerProvider.updateScannerUI.call(this, {
					actionEvent: ScannerAction.REGISTER,
					actionText: TranslationKey.SCANNER_ACTION_REGISTER,
					titleText: TranslationKey.SCANNER_TITLE_NOT_CONNECTED,
					error: TranslationKey.SCANNER_DESC_NOT_CONNECTED,
				});
			}
		} else {
			if (state.state === ScannerState.DISCONNECTED) {
				return ScannerProvider.updateScannerUI.call(this, {
					actionEvent: ScannerAction.RELEASE,
					actionText: TranslationKey.SCANNER_ACTION_RELEASE,
					titleText: TranslationKey.SCANNER_TITLE_DISCONNECTED,
				});
			} else if (state.state === ScannerState.CONNECTED) {
				if (state.scanProcess === ScanProcess.SCANNING) {
					return ScannerProvider.updateScannerUI.call(this, {
						titleText: TranslationKey.SCANNER_TITLE_SCANNING,
					});
				} else if (state.scanProcess === ScanProcess.PROCESSING) {
					return ScannerProvider.updateScannerUI.call(this, {
						titleText: TranslationKey.SCANNER_TITLE_PROCESSING,
					});
				} else {
					return ScannerProvider.updateScannerUI.call(this, {
						actionEvent: ScannerAction.RELEASE,
						actionText: TranslationKey.SCANNER_ACTION_RELEASE,
						titleText: TranslationKey.SCANNER_TITLE_AVAILABLE,
					});
				}
			} else {
				return Promise.resolve();
			}
		}
	}

	private static updateScannerUI(this: JourneyContainer, { titleText, actionText, actionEvent, error }: { titleText: string, actionText?: string, actionEvent?: ScannerAction, error?: string }) {
		return TemplateService.getElements(this._container, {
			providerContainer: '[data-jcs-element="provider__container"]',
		})
			.then(({ providerContainer }) => TemplateService.getElements(providerContainer, {
				scannerContainer: '[data-jcs-element="scanner__container"]',
				action: '[data-jcs-element="scanner__action"]',
				title: '[data-jcs-element="scanner__box__title"]',
				errorContainer: '[data-jcs-element="scanner__error"]',
				errorBusy: '[data-jcs-element="scanner__error__busy"]',
				errorOffline: '[data-jcs-element="scanner__error__offline"]',
				scannerAnimation: '[data-jcs-element="scanner__animation"]',
				imageProcessing: '[data-jcs-element="image__processing"]',
				errorImage: '[data-jcs-element="error__image"]',
				successImage: '[data-jcs-element="success__image"]',
				failureReasonMessage: '[data-jcs-element="failure__reason__message"]',
				tryAgainMessage: '[data-jcs-element="try__again__message"]',
				attemptCountContainer: '[data-jcs-element="attempt__count__container"]',
				attemptCounter: '[data-jcs-element="attempt__counter"]',
				failureReasonContainer: '[data-jcs-element="failure__reason__section"]'
			}))
			.then(({ scannerContainer, title, action, errorContainer, errorBusy, errorOffline, scannerAnimation, imageProcessing, errorImage, successImage, failureReasonMessage, tryAgainMessage, attemptCountContainer, attemptCounter, failureReasonContainer }) => {
				RenderingService.setText(title, LanguageService.getTranslation(titleText as TranslationKey));
				RenderingService.hideObject(successImage);
				RenderingService.hideObject(errorImage);
				ScannerProvider.scannerAnimation!.stop();
				// ScannerProvider.loadingAnimation!.stop();
				if (error) {
					RenderingService.showObject(errorContainer);
					RenderingService.addClass(scannerContainer, 'error');
					RenderingService.addClass(title, 'scanner-error-text');
					RenderingService.addClass(scannerAnimation, 'animation-error');
					RenderingService.removeClass(scannerAnimation, 'animation-success');
					RenderingService.removeClass(scannerAnimation, 'animation-scanning');
					RenderingService.removeClass(title, 'scanner-success-text');
					RenderingService.showObject(errorImage);
					if (error === TranslationKey.SCANNER_DESC_BUSY) {
						RenderingService.hideObject(errorOffline);
						RenderingService.showObject(errorBusy);
					} else {
						RenderingService.showObject(errorOffline);
						RenderingService.hideObject(errorBusy);
					}
				} else {
					RenderingService.hideObject(errorContainer);
					RenderingService.removeClass(scannerContainer, 'error');
					RenderingService.removeClass(title, 'scanner-error-text');
					RenderingService.removeClass(scannerAnimation, 'animation-error');
					RenderingService.addClass(title, 'scanner-success-text');
					RenderingService.addClass(scannerAnimation, 'animation-success');
					if (titleText === TranslationKey.SCANNER_TITLE_SCANNING) {
						ScannerProvider.scannerAnimation!.play();
					} else {
						ScannerProvider.scannerAnimation!.stop();
					}
				}
				if (titleText === TranslationKey.SCANNER_TITLE_AVAILABLE) {
					RenderingService.showObject(successImage);
				}

				if (titleText === TranslationKey.SCANNER_TITLE_PROCESSING) {
					ScannerProvider.loadingAnimation!.play();
					RenderingService.hideObject(scannerAnimation);
					RenderingService.showObject(imageProcessing);
				} else {
					RenderingService.showObject(scannerAnimation);
					RenderingService.hideObject(imageProcessing);
				}

				if (this._journey) {

					switch (this._journey.actionAttempt) {
						case 2:
							ScannerProvider.setTriplescanReasonMessages(this._journey.tripleScanReason, failureReasonMessage, tryAgainMessage, failureReasonContainer);
							RenderingService.setText(attemptCounter, '2');
							break;
						case 3:
							ScannerProvider.setTriplescanReasonMessages(this._journey.tripleScanReason, failureReasonMessage, tryAgainMessage, failureReasonContainer);
							RenderingService.setText(attemptCounter, '1');
							break;
						default:
							RenderingService.hideObject(failureReasonContainer);
							RenderingService.hideObject(attemptCountContainer);
							break;
					}


				}

				if (action instanceof HTMLButtonElement) {
					if (actionText && actionEvent) {
						RenderingService.setText(action, LanguageService.getTranslation(actionText as TranslationKey));
						RenderingService.setEnabled(action, true);

						action.removeEventListener('click', () => { /* NO-OP */ });
						action.addEventListener('click', () => {
							RenderingService.setEnabled(action as HTMLButtonElement, false);
							RenderingService.setText(action as HTMLButtonElement, LanguageService.getTranslation(TranslationKey.SCANNER_DESC_LOADING));

							switch (actionEvent) {
								case ScannerAction.RELEASE:
									SocketService.release();
								case ScannerAction.REGISTER:
									SocketService.tryRegistering();
							}
						});
					} else {
						RenderingService.setEnabled(action, false);
						RenderingService.setText(action, LanguageService.getTranslation(TranslationKey.SCANNER_DESC_LOADING));
					}
				}
			}).catch(() => { /*
				We don't care about the errors in here, it's likely the
				element has been de-rendered if an exception occurs
			*/ });
	}

	/**
	 * Stop socket communications
	 */
	private static stopSocket(this: JourneyContainer) {
		SocketService.removeAllListeners(SocketEvents.UPDATE);
		SocketService.removeAllListeners(SocketEvents.RESULT);
		return SocketService.stop()
			.then((stopped) => {
				if (stopped) {
					ScannerProvider._socketReady = false;
				}
			});
	}

	private static releaseTerminal(this: JourneyContainer) {
		return SocketService.resetTerminal()
			.then((stopped) => {
				if (stopped) {
					ScannerProvider._socketReady = false;
				}
			});
	}

	private static onSocketUpdate(this: JourneyContainer, update: any) {
		ScannerProvider.processMessage.call(this, update)
			.then(() => EventService.publish(JourneyEvent.SCANNER_UPDATE, {
				process: update.scanProcess,
				state: update.state,
			}))
			.catch(console.error);
	}

	private static onSocketResult(this: JourneyContainer, res: any) {
		res.entryData = {
			scanReference: res.scanReference, blurCheck: res.entryData['qualityChecks.BlurCheck'],
			glareCheck: res.entryData['qualityChecks.GlareCheck'], lowResolutionCheck: res.entryData['qualityChecks.LowResolutionCheck'],
			fullDocumentInViewCheck: res.entryData['qualityChecks.FullDocumentInViewCheck'], tripleScanReason: res.entryData.tripleScanReason
		};

		DataService.getDataStore<IIdScanData>(DataStoreType.ID_SCAN)
			.update(ScannerProvider.parseResponseData(res));

		if (res.requiredAction === 'ADDRESSDOCUMENT' ||
			res.requiredAction === 'SELFIE' ||
			res.requiredAction === 'LIVENESS' ||
			res.requiredAction === 'COVIDPASS' ||
			res.requiredAction === 'DATACHECKS') {
			ScannerProvider.releaseTerminal.call(this);
		}
		return ScannerProvider.stopSocket.call(this)
			.then(() => this._journey!.bindUpdate(res))
			.then(() => EventService.publish(JourneyEvent.SCANNER_RESULT, {
				currentResult: res.currentResult
			}))
			.then(() => this.evaluate())
			.then(this.updateJourneyFields)
			.catch(console.error);
	}

	private static parseResponseData = ({
		currentResult,
		highLevelResult,
		documentType,
		requiredAction,
		scanReference,
		entryData,
		isFinished,
	}: any): Partial<IIdScanData> => ({
		documents: [{
			type: documentType,
			scanReference
		}],
		blurCheck: entryData.blurCheck,
		glareCheck: entryData.glareCheck,
		lowResolutionCheck: entryData.lowResolutionCheck,
		fullDocumentInViewCheck: entryData.fullDocumentInViewCheck,
		tripleScanReason: entryData.tripleScanReason,
		requiredAction,
		highLevelResult,
		currentResult,
		isFinished
	})

	private static setTriplescanReasonMessages(tripleScanReason: string, failureReasonMessage: HTMLElement | undefined, tryAgainMessage: HTMLElement | undefined, failureReasonContainer: HTMLElement | undefined) {
		RenderingService.showObject(failureReasonContainer);

		switch (tripleScanReason) {
			case HighLevelResult.NotSupported:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_NOT_SUPPORTED));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_NOT_SUPPORTED));
				break;
			case HighLevelResult.Refer:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_REFER));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_REFER));
				break;
			case HighLevelResult.NotAccepted:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_NOT_ACCEPTED));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_NOT_ACCEPTED));

				break;
			case HighLevelResult.Expired:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_EXPIRED));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_EXPIRED));

				break;
			default:
				RenderingService.setText(failureReasonMessage, LanguageService.getTranslation(TranslationKey.FAILURE_REASON_NOT_SUPPORTED));
				RenderingService.setText(tryAgainMessage, LanguageService.getTranslation(TranslationKey.PLEASE_TRY_AGAIN_NOT_SUPPORTED));
				break;
		}
	};
}
